import {Component, Injector, OnInit} from '@angular/core';
import {BaseComponent} from "../../base.component";
import {ListResponse} from "../../../network/service/list.response";
import {SnackBarUtil} from "../../../util/snack.bar.util";
import {RequestType} from "../../../enums/request.type";
import {FlatTreeControl} from "@angular/cdk/tree";
import {MatTreeFlatDataSource, MatTreeFlattener} from "@angular/material/tree";
import {Config} from "../../../common/config";
import {VerifyOptions} from "../../dialog/verify/options/verify.options";
import {VerifyComponent} from "../../dialog/verify/verify.component";
import {DataResponse} from "../../../network/service/data.response";
import {AddOrUpdateBusinessOptions} from "../dialog/add-or-update-business/options/add-or-update-business.options";
import {AddOrUpdateBusinessComponent} from "../dialog/add-or-update-business/add-or-update-business.component";
import {RouteUtil} from "../../../util/route.util";

interface Business {
  BusinessId: string;
  Title: string;
  ParentId: string | null;
  Icon: string | null;
  Type: string | null;
  Details: string | null;
  StandardCode: string | null;
  Priority: number | null;
  CreatorId: string | null;
  CreateDate: string;
  ModifierId: string | null;
  ModifyDate: string | null;
  Children?: Business[];
}

interface BusinessFlatNode {
  expandable: boolean;
  BusinessId: string;
  Title: string;
  ParentId: string | null;
  Details: string | null;
  StandardCode: string | null;
  Priority: number | null;
  Type: string | null;
  level: number;
}

@Component({
  selector: 'app-businesses',
  standalone: false,
  templateUrl: './businesses.component.html',
  styleUrls: ['./businesses.component.scss']
})
export class BusinessesComponent extends BaseComponent implements OnInit {
  businesses?: any = [];

  constructor(injector: Injector) {
    super(injector);
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  override loadOnline() {
    super.loadOnline();
    this.serviceApi.getBusinesses(this.tableProperties.offset, this.tableProperties.limit, 'Parent', RequestType.ALL, {
      Order: this.tableProperties.order ?? {
        Field: 'ModifyDate',
        Type: 'DESC'
      }
    }).subscribe((res: ListResponse<any>) => {
      if (res.success) {
        this.businesses = res.data;
        let temp = this.convertToTree(this.businesses, Config.userInformation.BusinessId);
        let rootBusiness = this.businesses.find((business: any) => {
          return business.BusinessId === Config.userInformation.BusinessId;
        });
        this.dataSource.data = [{
          ...rootBusiness,
          Children: temp
        }];
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
  }

  convertToTree(data: any[], parentId: string | null = null): [] {
    const tree: any = [];
    data.forEach((item) => {
      if (item.ParentId === parentId) {
        const node: any = {
          ...item,
          Children: this.convertToTree(data, item.BusinessId),
        };
        tree.push(node);
      }
    });
    return tree;
  }

  onCreateClicked(businessId: string) {
    let addOrUpdateBusinessOptions = new AddOrUpdateBusinessOptions();
    addOrUpdateBusinessOptions.business = {};
    addOrUpdateBusinessOptions.parentId = businessId;
    addOrUpdateBusinessOptions.onInsertOrUpdateFinished = () => {
      this.loadOnline();
    };
    let addOrUpdateBusinessComponent = this.matDialog.open(AddOrUpdateBusinessComponent, {
      height: 'auto',
      width: '100%',
      direction: "rtl"
    });
    addOrUpdateBusinessComponent.componentInstance.addOrUpdateBusinessOptions = addOrUpdateBusinessOptions;
  }

  onUpdateClicked(business: any) {
    let addOrUpdateBusinessOptions = new AddOrUpdateBusinessOptions();
    addOrUpdateBusinessOptions.business = business;
    addOrUpdateBusinessOptions.onInsertOrUpdateFinished = () => {
      this.loadOnline();
    };
    let addOrUpdateBusinessComponent = this.matDialog.open(AddOrUpdateBusinessComponent, {
      height: 'auto',
      width: '100%',
      direction: "rtl"
    });
    addOrUpdateBusinessComponent.componentInstance.addOrUpdateBusinessOptions = addOrUpdateBusinessOptions;
  }

  onDeleteClicked(businessId: number) {
    let verifyOptions = new VerifyOptions();
    verifyOptions.message = 'آیا از حذف درخواست خود مطمئن هستید؟';
    let verifyComponent = this.matDialog.open(VerifyComponent);
    verifyOptions.onNoClicked = () => {
      verifyComponent.close();
    };
    verifyOptions.onYesClicked = () => {
      this.serviceApi.deleteBusinesses(businessId).subscribe((res: DataResponse<any>) => {
        if (res.success) {
          verifyComponent.close();
          this.loadOnline();
        } else {
          SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
        }
      });
    };
    verifyComponent.componentInstance.verifyOptions = verifyOptions;
  }

  onAssignRoleToBusiness(business: any) {
    this.router.navigate([RouteUtil.DASHBOARD_MODULE, RouteUtil.ROLES_COMPONENT, {
      BusinessId: business.BusinessId,
      BusinessTitle: business.Title
    }]).then();
  }

  hasChild = (_: number, node: BusinessFlatNode) => node.expandable;

  private _transformer = (node: Business, level: number) => {
    return {
      expandable: !!node.Children && node.Children.length > 0,
      BusinessId: node.BusinessId,
      ParentId: node.ParentId,
      Type: node.Type,
      StandardCode: node.StandardCode,
      Priority: node.Priority,
      Title: node.Title,
      Details: node.Details,
      level: level,
    };
  };
  treeControl = new FlatTreeControl<BusinessFlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );
  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.Children
  );
  dataSource: any = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
}
